/**
 * Where the transcript sits when a round docks (controller rulings on the F3
 * report, 2026-10-13; plan 2026-10-13 "Follow-up rounds").
 *
 * The docked card is capped at 70dvh, which leaves the transcript a 74–103 px
 * band at common heights (1280×720, 390×844). Pinned to the very bottom, that
 * band shows the turn's pills and action row, while the turn's last line of
 * text ends above it — a follow-up's "the main unknown is the room size…", or
 * a lead-in at 1280×720. So MessageList pins to the bottom as always, and then,
 * for every docked round, sits the end of the docking turn's TEXT at the bottom
 * of the band, right above the card. A user who scrolled up and an undocked
 * turn keep today's behaviour (MessageList decides those).
 */

/**
 * One line of answer text (15px at a 1.55–1.62 line height). The text's end
 * counts as already in view when at least this much of it is in the band.
 */
export const ANSWER_LINE_PX = 24;

/** Marks the text of a COMPLETED assistant turn (never the in-flight one). */
export const ANSWER_TEXT_TEST_ID = "chat-answer-text";

export interface AnswerEndInput {
  /** The scroller's current scrollTop (pinned to the bottom when this runs). */
  scrollTop: number;
  /** The scroller's visible band, in viewport coordinates. */
  bandTop: number;
  bandBottom: number;
  /**
   * The OUTER bottom of the answer-text element (the design bubble's border
   * edge), in viewport coordinates, where it lands. This is what the anchor
   * puts at the bottom of the band.
   */
  textBottom: number;
  /**
   * How far the last line ends above `textBottom`: the element's bottom
   * padding plus its bottom border (15 px for the design bubble; 0 for the
   * plain text block). The guard tests the TEXT, not the padding.
   */
  textInset?: number;
}

/**
 * The scrollTop that puts the end of the answer text at the bottom of the band,
 * or null when nothing should move: its last line is already in view, or it
 * is below the band (the pin rule's business — never the case while pinned).
 *
 * Rounded UP to a whole pixel: the band's edges are often fractional (a
 * 102.75 px band at 390×844), the browser keeps a whole-pixel scrollTop, and
 * rounding down would leave the last line a fraction of a pixel under the card.
 */
export function answerEndScrollTop({
  scrollTop,
  bandTop,
  bandBottom,
  textBottom,
  textInset = 0,
}: AnswerEndInput): number | null {
  if (textBottom > bandBottom) return null;
  if (textBottom - textInset >= bandTop + ANSWER_LINE_PX) return null;
  return Math.max(0, Math.ceil(scrollTop - (bandBottom - textBottom)));
}

/** A computed length in px (0 when it is not one). */
function px(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The vertical translation `transform` applies, in px (0 for none or a form
 * this cannot read). Browsers report a computed transform as a matrix.
 */
function translateYOf(transform: string): number {
  const match = /^matrix(3d)?\((.+)\)$/.exec(transform.trim());
  if (match === null) return 0;
  const values = (match[2] ?? "").split(",").map((value) => Number(value.trim()));
  const ty = match[1] === undefined ? values[5] : values[13];
  return ty !== undefined && Number.isFinite(ty) ? ty : 0;
}

/**
 * The scroll target for the scroller's LAST completed answer text — the turn a
 * docked round rides, since a round docks only on the last turn — or null.
 *
 * A just-committed turn enters with an 8 px slide (`bubbleMotion`), so its
 * drawn position is up to 8 px below where it lands; the translation of every
 * element between the text and the scroller is taken off.
 */
export function measureAnswerEndScrollTop(scroller: HTMLElement): number | null {
  const texts = scroller.querySelectorAll<HTMLElement>(`[data-testid="${ANSWER_TEXT_TEST_ID}"]`);
  const text = texts[texts.length - 1];
  if (text === undefined) return null;
  let slide = 0;
  for (let node: HTMLElement | null = text; node !== null && node !== scroller; node = node.parentElement) {
    slide += translateYOf(getComputedStyle(node).transform);
  }
  const band = scroller.getBoundingClientRect();
  const style = getComputedStyle(text);
  return answerEndScrollTop({
    scrollTop: scroller.scrollTop,
    bandTop: band.top,
    bandBottom: band.bottom,
    textBottom: text.getBoundingClientRect().bottom - slide,
    textInset: px(style.paddingBottom) + px(style.borderBottomWidth),
  });
}
